'use client';

import CustomButton from '@/components/custom-button';
import CustomPage from '@/components/custom-page';
import CustomRePassword from '@/components/custom-re-password';
import CustomForm from '@/components/custom-RHF-form';
import CustomToast from '@/components/custom-toast';
import { template_error } from '@/components/custom-toast/sonner-with-template';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { regexUserName } from '@/constant/regexs';
import { pageDirectories } from '@/constant/route';
import { MessageResource } from '@/i18n/messages';
import { cn } from '@/lib/utils';
import { Link, useRouter } from '@/navigation';
import { forgotPassword } from '@/server/api/v1/auth/auth.action';
import { ResetCodeType } from '@/types/enum';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronLeft, CircleAlert } from 'lucide-react';
import { useTranslations } from 'next-intl';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import Balancer from 'react-wrap-balancer';
import { z } from 'zod';
import { BackgroundSignIn } from './bg-sign-in';

interface ForgorFormProps extends React.HTMLAttributes<HTMLDivElement> {}

const { sonner } = CustomToast;

const ForgotPasswordSchema = (
    t: (msg: keyof MessageResource['Forgor']) => string
) => {
    return z.object({
        userName: z
            .string()
            .min(4, {
                message: t('Forgor_UsernameMustBeAtLeast4CharactersLong'),
            })
            .regex(regexUserName, {
                message: t('Forgor_InvalidUserNamePlsCheckAgain'),
            }),

        email: z.string().optional(),
    });
};

type ForgotPassword = z.infer<ReturnType<typeof ForgotPasswordSchema>>;

export function ForgorForm({ className, ...props }: ForgorFormProps) {
    const transDef = useTranslations('System');
    const transPage = useTranslations('Forgor');

    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [isHandleNewPwd, setIsHandleNewPwd] = React.useState<boolean>(false);
    const [forgotInfo, setForgotInfo] = React.useState<{
        userName: string;
        email?: string;
    }>({
        userName: '',
    });
    const [status, setStatus] = React.useState<{
        isSuccess: boolean;
        isError: boolean;
        message?: string;
    }>({ isError: false, isSuccess: false });

    const router = useRouter();

    const methods = useForm({
        mode: 'onBlur',
        resolver: zodResolver(ForgotPasswordSchema(transPage)),
        defaultValues: { userName: '', email: '' },
    });

    const onGoOutToLogin = () => {
        window.location.href = pageDirectories['Signin'];
    };

    const onSubmit = async (data: ForgotPassword) => {
        setIsLoading(true);

        const result = await forgotPassword({
            ...data,
            type: ResetCodeType.OTP,
        });

        if (result?.data?.isSuccessStatus) {
            setIsHandleNewPwd(true);
            setForgotInfo({
                userName: data.userName,
                email: data.email,
            });
        } else {
            setIsHandleNewPwd(false);
            setForgotInfo({ userName: '' });
            sonner.error(
                template_error({
                    title: transDef('System_AnErrorHasOccurred'),
                    desc: result?.data?.errors,
                })
            );
            setIsLoading(false);
        }
    };

    const onSuccess = async () => {
        setStatus({
            isSuccess: true,
            isError: false,
        });
    };

    const onError = async (errs: string[]) => {
        setStatus({
            isSuccess: false,
            isError: true,
            message: errs.join(', ') ?? transPage('Forgor_PlsTryAgain'),
        });
    };

    const children = () => {
        if (status.isSuccess) {
            return (
                <div className='w-full px-4 py-14'>
                    <div className='relative flex items-center justify-center'>
                        <div className='h-20 w-20 animate-ping items-center justify-center rounded-full bg-green-200 p-6 duration-1000'></div>
                        <div className='absolute rounded-full bg-green-500 p-4'>
                            <svg
                                xmlns='http://www.w3.org/2000/svg'
                                fill='none'
                                viewBox='0 0 24 24'
                                strokeWidth='1.5'
                                stroke='currentColor'
                                className='h-8 w-8 text-white'
                            >
                                <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    d='M4.5 12.75l6 6 9-13.5'
                                />
                            </svg>
                        </div>
                    </div>
                    <h3 className='my-4 text-center text-3xl font-semibold text-gray-700'>
                        {transPage('Forgor_SucessfullyChangePassword')}!!!
                    </h3>

                    <Link
                        href={pageDirectories['Signin']}
                        className='block text-center text-sm text-primary hover:underline'
                    >
                        {transPage('Forgor_BackToLogin')}
                    </Link>
                </div>
            );
        }

        if (isHandleNewPwd) {
            return (
                <div
                    className={cn(
                        'mx-auto grid w-full max-w-md gap-6',
                        className
                    )}
                    {...props}
                >
                    <div className='text-sm text-primary'>
                        <button
                            role='button'
                            onClick={() => {
                                setIsHandleNewPwd(false);
                                setIsLoading(false);
                                setStatus({ isError: false, isSuccess: false });
                                setForgotInfo({
                                    userName: '',
                                    email: undefined,
                                });
                            }}
                            className='flex items-center gap-x-2'
                        >
                            <ChevronLeft size={14} />
                            {transPage('Forgor_GoBack')}
                        </button>
                    </div>
                    <h3 className='py-6 text-center text-3xl font-semibold tracking-wide'>
                        {transPage('Forgor_ResetPassword')}
                    </h3>
                    <CustomRePassword.WithOTP
                        username={forgotInfo.userName}
                        email={forgotInfo.email}
                        onSuccess={onSuccess}
                        onError={onError}
                        onClearAlert={() =>
                            setStatus({ isError: false, isSuccess: false })
                        }
                        alert={
                            status.isError && (
                                <Alert
                                    className={cn('py-2', {
                                        'bg-red-100 text-red-600':
                                            status.isError,
                                    })}
                                >
                                    <CircleAlert className={cn('h-4 w-4')} />
                                    <AlertTitle className='text-sm'>
                                        {transDef(
                                            'System_AnErrorHasOccurred'
                                        ) ?? ''}
                                    </AlertTitle>
                                    <AlertDescription className='text-xs'>
                                        {status.message ?? ''}
                                    </AlertDescription>
                                </Alert>
                            )
                        }
                    />
                </div>
            );
        }

        return (
            <div
                className={cn('mx-auto grid w-full max-w-md gap-6', className)}
                {...props}
            >
                <div className='flex flex-col space-y-4 text-center'>
                    <h1 className='text-2xl font-semibold tracking-tight'>
                        <Balancer>{transPage('Forgor_PageTitle')}</Balancer>
                    </h1>
                    <p className='text-sm text-muted-foreground'>
                        <Balancer>
                            {transPage(
                                'Forgor_EnterRegistrationAccountNameInformation'
                            )}
                        </Balancer>
                    </p>
                </div>
                <CustomForm.FormProvider {...methods}>
                    <CustomForm.Form<ForgotPassword>
                        rls={[
                            {
                                type: 'Item',
                                name: 'userName',
                                children: (
                                    <Input
                                        placeholder={
                                            transPage(
                                                'Forgor_EnterYourAccountName'
                                            ) + '...'
                                        }
                                        className='no-ring shadow-md'
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                methods.handleSubmit(
                                                    onSubmit
                                                )();
                                            }
                                        }}
                                        disabled={isLoading}
                                    />
                                ),
                            },
                        ]}
                    />

                    <CustomButton.Root
                        isLoading={isLoading}
                        onClick={methods.handleSubmit(onSubmit)}
                        className='shadow-md'
                    >
                        {isLoading && (
                            <div className='mr-2 h-4 w-4 animate-spin' />
                        )}
                        {transPage('Forgor_Next')}
                    </CustomButton.Root>
                </CustomForm.FormProvider>
                <div className='my-4 flex justify-center text-xs'>
                    <span className='px-1 text-muted-foreground'>
                        {transPage('Forgor_HaveAccount')}
                    </span>
                    <Link
                        href={pageDirectories.Signin}
                        className='font-bold hover:text-primary'
                    >
                        {transPage('Forgor_BackToLogin')}
                    </Link>
                </div>
            </div>
        );
    };

    return (
        <div className='relative flex min-h-full min-w-full bg-secondary/40 lg:overflow-y-hidden'>
            <CustomPage.Wrapper className='relative z-10 w-full'>
                <div className='flex h-full w-full flex-col items-center justify-center px-4 py-24 sm:px-6 lg:px-8'>
                    {children()}
                </div>
            </CustomPage.Wrapper>

            <BackgroundSignIn />
        </div>
    );
}
